import { toast } from 'sonner';
import { T, useGT } from 'gt-react';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useRematchRemittance } from '@/api/rpc/hooks/accounts-receivable/useArRemittances';
import { Button } from '@/components/ui/button';
import { rematchNotices, type RematchNotice } from './rematchNotices';

interface RemittanceMatchActionsProps {
  remittanceId: string;
  disabled: boolean;
}

export function RemittanceMatchActions({
  remittanceId,
  disabled,
}: RemittanceMatchActionsProps) {
  const gt = useGT();
  const rematchMutation = useRematchRemittance();

  const showNotice: {
    [K in RematchNotice['kind']]: (
      notice: Extract<RematchNotice, { kind: K }>,
    ) => void;
  } = {
    source_changed: () =>
      toast.warning(
        gt('Remittance changed while reprocessing. Review it and try again.', {
          context: 'AR remittance changed during combined rematch',
        }),
      ),
    invoice_matching_failed: () =>
      toast.warning(
        gt(
          'Bank reconciliation finished, but invoice matching failed. Try Re-process again.',
          {
            context: 'AR remittance rematch partially completed',
          },
        ),
      ),
    memory_not_applied: (notice) =>
      toast.warning(
        gt(
          "Re-process complete, but this customer's memory notes couldn't be applied.",
          {
            context:
              'AR remittance reprocess where customer memory notes were rejected',
          },
        ),
        { description: notice.explanation },
      ),
    lines_need_review: () =>
      toast.warning(
        gt('Re-process complete. Some lines need review.', {
          context: 'remittance reprocess result requiring review',
        }),
      ),
    complete: () =>
      toast.success(
        gt('Re-process complete.', {
          context: 'AR remittance combined reprocess result',
        }),
      ),
  };

  function notify<K extends RematchNotice['kind']>(
    notice: Extract<RematchNotice, { kind: K }>,
  ) {
    const show: (value: typeof notice) => void = showNotice[notice.kind];
    show(notice);
  }

  async function rematch() {
    try {
      const result = await rematchMutation.mutateAsync({ id: remittanceId });
      rematchNotices(result).forEach(notify);
    } catch {
      toast.error(
        gt(
          'Re-process failed. Review the refreshed remittance and try again.',
          {
            context: 'AR remittance combined rematch error',
          },
        ),
      );
    }
  }

  return (
    <Button
      variant="outline"
      onClick={() => void rematch()}
      disabled={disabled || rematchMutation.isPending}
      aria-busy={rematchMutation.isPending}
      className="w-fit"
    >
      <ArrowsClockwiseIcon
        className={rematchMutation.isPending ? 'animate-spin' : ''}
        aria-hidden="true"
      />
      {rematchMutation.isPending ? (
        <T context="remittance reprocess loading state">Re-processing...</T>
      ) : (
        <T context="re-run bank reconciliation and invoice matching for a remittance">
          Re-process
        </T>
      )}
    </Button>
  );
}
